<template>
  <Dialog
    :visible="visible"
    :modal="true"
    :closable="true"
    :draggable="false"
    class="translation-modal"
    style="width: 600px; max-width: 90vw"
    @update:visible="handleClose"
    @hide="handleClose"
  >
    <template #header>
      <span class="modal-title">
        {{ isBatchMode ? t('templateBuilder.translation.batchTranslation') : t('templateBuilder.translation.singleTranslation') }}
      </span>
    </template>
    <div class="translation-modal-content">
      <!-- Mode correction de langue -->
      <div class="correction-mode-section">
        <!-- Toggle mode correction (toujours affiché) -->
        <div class="correction-mode-toggle">
          <div class="correction-mode-left">
            <Checkbox
              v-model="isCorrectionMode"
              input-id="correction-mode"
              :binary="true"
              :disabled="isSingleLanguageModel"
            />
            <label for="correction-mode" class="correction-mode-label">
              {{ t('templateBuilder.translation.languageCorrectionMode') }}
            </label>
          </div>
          <HelpMessage
            custom-class="translation-help-link"
            :link="{
              type: 'gleap',
              articleId: '168',
              label: t('showHelpArticle')
            }"
          />
        </div>

        <!-- Message pour modèle mono-langue -->
        <div v-if="isSingleLanguageModel" class="single-language-notice mb-4">
          <div class="p-3 border-l-4 border-orange-400 bg-orange-50 rounded">
            <div class="flex items-start">
              <i class="pi pi-exclamation-triangle text-orange-500 mr-2 mt-1" />
              <div>
                <p class="font-semibold text-orange-800 mb-1">{{ t('templateBuilder.translation.singleLanguageTitle') }}</p>
                <p class="text-sm text-orange-700">{{ t('templateBuilder.translation.singleLanguageMessage') }}</p>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="isCorrectionMode || isSingleLanguageModel"
          class="correction-mode-explanation"
        >
          <div class="explanation-content text-justify">
            <i class="pi pi-info-circle mr-2" />
              <p>{{ t('templateBuilder.translation.languageCorrectionExplanation') }}<br />
                {{ t('templateBuilder.translation.languageCorrectionExplanationExample') }}</p>
          </div>
        </div>
      </div>

      <!-- Langue source (mode normal) ou Langue à corriger (mode correction) -->
      <div class="source-section">
        <div class="section-title">
          <i class="pi pi-globe mr-2" />
          {{ isCorrectionMode ? t('templateBuilder.translation.correctionLanguage') : t('templateBuilder.translation.sourceLanguage') }}
        </div>
        <div class="source-dropdown">
          <Dropdown
            v-model="selectedSourceLanguage"
            :options="availableLanguages"
            option-label="label"
            option-value="value"
            :placeholder="t('templateBuilder.translation.selectSourceLanguage')"
            class="w-full"
          >
            <template #option="{ option }">
              <div class="flex items-center py-1">
                <img
                  :src="`/images/flags/${option.value}.png`"
                  :alt="option.label"
                  class="flag-icon"
                >
                <span class="language-label">{{ option.label }}</span>
              </div>
            </template>
            <template #value="{ value }">
              <div
                v-if="value"
                class="flex items-center"
              >
                <img
                  :src="`/images/flags/${value}.png`"
                  :alt="getLanguageLabel(value)"
                  class="flag-icon"
                >
                <span class="language-label">{{ getLanguageLabel(value) }}</span>
              </div>
            </template>
          </Dropdown>
        </div>
      </div>

      <!-- Langues cibles -->
      <div
        v-if="!isCorrectionMode && !isSingleLanguageModel"
        class="target-section"
      >
        <div class="section-title">
          <i class="pi pi-flag mr-2" />
          {{ t('templateBuilder.translation.targetLanguages') }}
        </div>
        <div class="target-languages-container">
          <div
            v-for="language in availableTargetLanguages"
            :key="language.value"
            :class="['language-item', { checked: selectedTargetLanguages.includes(language.value) }]"
            @click="toggleTarget(language.value)"
          >
            <Checkbox
              v-model="selectedTargetLanguages"
              :input-id="`target-lang-${language.value}`"
              :value="language.value"
              class="language-checkbox"
              @click.stop
            />
            <div class="language-label-container">
              <img
                :src="`/images/flags/${language.value}.png`"
                :alt="language.label"
                class="flag-icon"
              >
              <span class="language-label">{{ language.label }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Aperçu du texte à traduire ou liste des champs -->
      <div class="preview-section">
        <div class="section-title">
          <i class="pi pi-file-text mr-2" />
          {{ isBatchMode ? t('templateBuilder.translation.fieldsToTranslate') : t('templateBuilder.translation.textToTranslate') }}
        </div>
        <div class="preview-body">
          <div
            v-if="isBatchMode"
            class="batch-fields-container"
          >
            <div class="batch-summary">
              <p class="text-sm text-gray-600 mb-3">
                {{ t('templateBuilder.translation.batchSummary', { count: translatableFields.length }) }}
              </p>
            </div>
            <div class="fields-list">
              <div
                v-for="field in translatableFields"
                :key="field.key"
                class="field-item"
              >
                <div class="field-info">
                  <span class="field-type">{{ field.fieldType }}</span>
                </div>
                <div
                  v-if="field.value"
                  class="field-preview"
                >
                  {{ truncateText(field.value, 50) }}
                </div>
              </div>
            </div>
          </div>
          <div
            v-else
            class="text-preview-container"
          >
            <div
              class="text-preview"
              v-html="formattedTextToTranslate"
            />
          </div>
          <!-- Overlay de progression (batch et single) -->
          <div
            v-if="uiIsTranslating"
            class="progress-overlay"
          >
            <div class="overlay-card">
              <div class="progress-info">
                <i class="pi pi-spinner pi-spin mr-2" />
                {{ t('templateBuilder.translation.translating') }}...
              </div>
              <ProgressBar
                :value="uiProgress"
                :show-value="true"
                class="custom-progress-bar"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Indicateur de progression -->
      <div
        v-if="false"
        class="progress-section"
      >
        <div class="progress-info">
          <i class="pi pi-spinner pi-spin mr-2" />
          {{ t('templateBuilder.translation.translating') }}...
        </div>
        <ProgressBar
          :value="uiProgress"
          :show-value="true"
          class="custom-progress-bar"
        />
      </div>
    </div>

    <template #footer>
      <div class="modal-footer">
        <SpmButton
          :label="t('common.cancel')"
          icon="far fa-times"
          class-style="p-button-secondary p-button-sm"
          @click="handleClose"
        />
        <SpmButton
          :label="translateCtaLabel"
          icon="ai-layout-icon fa-light fa-language"
          :disabled="!canTranslate || uiIsTranslating"
          :loading="uiIsTranslating"
          class-style="p-button-sm ai-layout active"
          @click="handleTranslate"
        />
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts">
import {
  computed, defineComponent, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';
import ProgressBar from 'primevue/progressbar';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import HelpMessage from '@/components/common/HelpMessage.vue';
import { Translation } from '@/types';
import { Lang, UserState } from '@/composables/User';

interface TranslationRequest {
  sourceLanguage: string;
  targetLanguages: string[];
  translation: Translation;
}

interface LanguageOption {
  label: string;
  value: string;
}

export default defineComponent({
  name: 'TranslationModal',

  components: {
    Dialog,
    Dropdown,
    Checkbox,
    ProgressBar,
    SpmButton,
    HelpMessage,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },

    translation: {
      type: Object as () => Translation | null,
      default: null,
    },

    defaultLanguage: {
      type: String,
      required: true,
    },

    activeLanguage: {
      type: String,
      required: true,
    },

    isBatchMode: {
      type: Boolean,
      default: false,
    },

    translatableFields: {
      type: Array as () => Translation[],
      default: () => [],
    },

    // Pilotage du mode batch par le parent
    batchModeTranslating: {
      type: Boolean,
      default: false,
    },

    batchModeProgress: {
      type: Number,
      default: 0,
    },
  },

  emits: ['update:visible', 'translate'],

  setup(props, { emit }) {
    const { t } = useI18n();

    // États réactifs
    const selectedSourceLanguage = ref<string>('');
    const selectedTargetLanguages = ref<string[]>([]);
    const isTranslating = ref(false);
    const translationProgress = ref(0);
    const isCorrectionMode = ref(false);

    // États effectifs pour l'UI: en mode batch, on utilise les props du parent
    const uiIsTranslating = computed(() => (props.isBatchMode ? props.batchModeTranslating : isTranslating.value));
    const uiProgress = computed(() => (props.isBatchMode ? props.batchModeProgress : translationProgress.value));

    // Langues disponibles
    const availableLanguages = computed<LanguageOption[]>(() => UserState.activeShop?.langs.map((lang: Lang) => ({
      label: lang.id.toUpperCase(),
      value: lang.id,
    })) ?? []);

    // Détecter si le modèle n'a qu'une seule langue (force le mode correction)
    const isSingleLanguageModel = computed(() => availableLanguages.value.length === 1);

    // Langues cibles disponibles (exclut la langue source sélectionnée en mode normal)
    const availableTargetLanguages = computed<LanguageOption[]>(() => {
      if (isCorrectionMode.value || isSingleLanguageModel.value) {
        // En mode correction ou modèle mono-langue, on ne montre pas la section des langues cibles
        return [];
      }
      return availableLanguages.value.filter(
        (lang) => lang.value !== selectedSourceLanguage.value,
      );
    });

    // Texte à traduire
    const textToTranslate = computed(() => props.translation?.value || '');

    // Texte formaté pour l'affichage (décode les entités HTML)
    const formattedTextToTranslate = computed(() => {
      const text = textToTranslate.value;
      if (!text) return '';

      // Créer un élément temporaire pour décoder les entités HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = text;
      return tempDiv.innerHTML;
    });

    // Obtenir le label d'une langue par son code
    const getLanguageLabel = (languageCode: string): string => {
      const language = availableLanguages.value.find((lang) => lang.value === languageCode);
      return language?.label || languageCode.toUpperCase();
    };

    // Libellé dynamique du CTA
    const translateCtaLabel = computed(() => {
      if (isCorrectionMode.value) {
        const source = getLanguageLabel(selectedSourceLanguage.value);
        return source ? `Corriger le contenu ${source}` : t('templateBuilder.translation.translate');
      }

      const count = selectedTargetLanguages.value.length;
      const source = getLanguageLabel(selectedSourceLanguage.value);
      if (count === 0) {
        return t('templateBuilder.translation.selectTargetLanguage');
      }
      if (count > 0 && source) {
        return t('templateBuilder.translation.translateFromToCount', { source, count });
      }
      return t('templateBuilder.translation.translate');
    });

    // Peut traduire selon le mode
    const canTranslate = computed(() => {
      const hasSourceLanguage = selectedSourceLanguage.value;

      if (isCorrectionMode.value) {
        // En mode correction, on a juste besoin d'une langue source
        if (props.isBatchMode) {
          return hasSourceLanguage && props.translatableFields.length > 0;
        }
        return hasSourceLanguage && textToTranslate.value.trim() !== '';
      }

      // Mode normal : besoin de langues cibles différentes de la source
      const hasTargetLanguages = selectedTargetLanguages.value.length > 0;
      if (props.isBatchMode) {
        return hasTargetLanguages && hasSourceLanguage && props.translatableFields.length > 0;
      }
      return hasTargetLanguages && hasSourceLanguage && textToTranslate.value.trim() !== '';
    });

    // Fonction pour tronquer le texte
    const truncateText = (text: string, maxLength: number): string => {
      if (!text) return '';
      // Supprimer les balises HTML pour l'aperçu
      const plainText = text.replace(/<[^>]*>/g, '');
      if (plainText.length <= maxLength) return plainText;
      return `${plainText.substring(0, maxLength)}...`;
    };

    // Initialiser les sélections par défaut
    const initializeDefaults = () => {
      selectedSourceLanguage.value = props.defaultLanguage;

      // Si modèle mono-langue, forcer le mode correction
      if (isSingleLanguageModel.value) {
        isCorrectionMode.value = true;
        selectedTargetLanguages.value = [selectedSourceLanguage.value];
      } else {
        // Même comportement en single et batch: pré-sélectionner toutes les cibles (toutes sauf la source)
        selectedTargetLanguages.value = availableLanguages.value
          .map((lang) => lang.value)
          .filter((value) => value !== selectedSourceLanguage.value);
      }
    };

    // Watcher pour réinitialiser quand la modale s'ouvre
    watch(() => props.visible, (newVisible) => {
      if (newVisible) {
        initializeDefaults();
        isTranslating.value = false;
        translationProgress.value = 0;
        // Ne pas reset le mode correction si c'est un modèle mono-langue
        if (!isSingleLanguageModel.value) {
          isCorrectionMode.value = false;
        }
      }
    });

    // Forcer le mode correction pour les modèles mono-langue
    watch(() => isSingleLanguageModel.value, (isSingle) => {
      if (isSingle) {
        isCorrectionMode.value = true;
      }
    }, { immediate: true });

    // Watcher pour le mode correction
    watch(isCorrectionMode, (newCorrectionMode) => {
      if (newCorrectionMode) {
        // En mode correction, la langue cible devient la même que la source
        selectedTargetLanguages.value = [selectedSourceLanguage.value];
      } else {
        // En mode normal, réinitialiser les langues cibles (exclure la source)
        selectedTargetLanguages.value = availableLanguages.value
          .map((lang) => lang.value)
          .filter((value) => value !== selectedSourceLanguage.value);
      }
    });

    // Watcher pour mettre à jour les langues cibles si la source change (mode normal uniquement)
    watch(selectedSourceLanguage, (newSource) => {
      if (isCorrectionMode.value) {
        // En mode correction, la cible suit automatiquement la source
        selectedTargetLanguages.value = [newSource];
      } else {
        // En mode normal, retirer la nouvelle langue source des langues cibles si elle y était
        selectedTargetLanguages.value = selectedTargetLanguages.value.filter(
          (lang) => lang !== newSource,
        );
      }
    });

    // Toggle d'une langue cible lors du clic sur la ligne entière
    const toggleTarget = (value: string) => {
      const idx = selectedTargetLanguages.value.indexOf(value);
      if (idx >= 0) {
        selectedTargetLanguages.value.splice(idx, 1);
      } else {
        selectedTargetLanguages.value.push(value);
      }
    };

    // Gérer la fermeture de la modale
    const handleClose = (value?: boolean) => {
      if (typeof value === 'boolean') {
        emit('update:visible', value);
      } else {
        emit('update:visible', false);
      }
    };

    // Gérer la traduction
    const handleTranslate = async () => {
      // En mode batch, props.translation est nul par conception. N'autoriser le retour anticipé
      // que si nous ne sommes PAS en mode batch et qu'il manque la traduction.
      if (!canTranslate.value || (!props.isBatchMode && !props.translation)) {
        return;
      }

      if (!props.isBatchMode) {
        isTranslating.value = true;
        translationProgress.value = 0;
      }

      // Déterminer l'objet Translation à inclure dans la requête.
      // En mode batch, utiliser le premier champ traduisible (il existe car canTranslate est vrai en batch).
      const baseTranslation = (props.isBatchMode
        ? (props.translatableFields[0] as Translation)
        : (props.translation as Translation));

      // En mode correction, les langues cibles sont automatiquement définies
      const targetLanguages = isCorrectionMode.value
        ? [selectedSourceLanguage.value]
        : selectedTargetLanguages.value;

      const translationRequest: TranslationRequest = {
        sourceLanguage: selectedSourceLanguage.value,
        targetLanguages,
        translation: baseTranslation,
      };

      try {
        // Émettre l'événement de traduction
        emit('translate', translationRequest);

        // En mode batch, le parent gère la progression et la fermeture
        if (!props.isBatchMode) {
          // Calculer le progrès basé sur le nombre de langues cibles (simulation simple)
          const totalLanguages = targetLanguages.length;
          const progressStep = 100 / Math.max(totalLanguages, 1);

          let currentProgress = 0;
          const progressInterval = setInterval(() => {
            currentProgress += progressStep;
            translationProgress.value = Math.min(currentProgress, 100);

            if (translationProgress.value >= 100) {
              clearInterval(progressInterval);
              setTimeout(() => {
                isTranslating.value = false;
                handleClose();
              }, 500);
            }
          }, 300);
        }
      } catch (error) {
        console.error('Erreur lors de la traduction:', error);
        if (!props.isBatchMode) {
          isTranslating.value = false;
          translationProgress.value = 0;
        }
      }
    };

    return {
      t,
      selectedSourceLanguage,
      selectedTargetLanguages,
      uiIsTranslating,
      uiProgress,
      availableLanguages,
      availableTargetLanguages,
      formattedTextToTranslate,
      canTranslate,
      translateCtaLabel,
      getLanguageLabel,
      truncateText,
      handleClose,
      handleTranslate,
      toggleTarget,
      isCorrectionMode,
      isSingleLanguageModel,
    };
  },
});
</script>

<style lang="scss" scoped>
.translation-modal {
  .translation-modal-content {
    // Section mode correction avec icône d'aide
    .correction-mode-section {

      .correction-mode-toggle {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .correction-mode-left {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .translation-help-link {
          flex-shrink: 0;
        }
      }
    }
    // Sections principales
    .source-section, .target-section, .preview-section, .progress-section {
      margin-bottom: 2rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    // Overlay de progression (batch + single) - limité au contenu (pas le titre)
    .preview-body {
      position: relative;
    }

    .progress-overlay {
      position: absolute;
      inset: 0;
      background-color: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(2px);
      z-index: 2;
      display: flex;
      align-items: center; // Center vertically
      justify-content: center; // Center horizontally
      padding: 1rem;
      border-radius: 0.5rem;
    }

    .overlay-card {
      width: 100%;
      max-width: 520px;
      background: rgba(255, 255, 255, 0.95);
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
      padding: 0.75rem 1rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .overlay-card .progress-info {
      display: flex;
      align-items: center; // vertically center spinner & text
      justify-content: center; // center horizontally
      gap: 0.5rem;
      color: #374151;
      font-weight: 500;
    }

    // Titres des sections
    .section-title {
      font-size: 1rem;
      font-weight: 600;
      color: #374151;
      margin-bottom: 1rem;
      display: flex;
      align-items: center;

      i {
        color: #94c840;
        font-size: 0.875rem;
      }
    }

    // Dropdown langue source
    .source-dropdown {
      width: 100%;
    }

    // Progress bar styling: white percentage text
    .custom-progress-bar {
      .p-progressbar-label {
        color: #ffffff !important;
        font-weight: 600;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
        display: flex;
        align-items: center; // vertical centering
        justify-content: center;
        height: 100%;
      }
    }

    // Icônes de drapeaux - TAILLE CORRECTE
    .flag-icon {
      width: 20px;
      height: 15px;
      margin-right: 0.75rem;
      object-fit: cover;
      border-radius: 2px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      flex-shrink: 0;
    }

    // Labels des langues
    .language-label {
      font-size: 0.875rem;
      font-weight: 500;
      color: #374151;
    }

    // Container des langues cibles
    .target-languages-container {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      gap: 0.75rem;
      padding: 1rem;
      background-color: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      max-height: 200px;
      overflow-y: auto;
    }

    // Items de langue cible
    .language-item {
      display: flex;
      align-items: center;
      padding: 0.5rem;
      background-color: #ffffff;
      border-radius: 0.375rem;
      border: 1px solid #e5e7eb;
      transition: all 0.2s ease;

      &:hover {
        border-color: #94c840;
        background-color: #f7fbef; // subtle greenish background on hover
        box-shadow: 0 1px 3px rgba(148, 200, 64, 0.12);
      }

      &.checked {
        border-color: #94c840;
        background-color: #f4faea; // slightly stronger when selected
      }
    }

    // Checkbox des langues
    .language-checkbox {
      margin-right: 0.5rem;
    }

    // PrimeVue checkbox customizations (target the root element that has both classes)
    :deep(.language-checkbox.p-checkbox) {
      display: inline-flex;
      align-items: center;
    }

    // Container du label avec drapeau
    .language-label-container {
      display: flex;
      align-items: center;
      cursor: pointer;
      flex: 1;
    }

    // Container de prévisualisation du texte
    .text-preview-container {
      padding: 1rem;
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      background-color: #fafafa;
      max-height: 120px;
      overflow-y: auto;
    }

    // Container pour le mode batch
    .batch-fields-container {
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      background-color: #fafafa;

      .batch-summary {
        padding: 1rem 1rem 0 1rem;

        p {
          margin: 0;
          font-weight: 500;
          color: #374151;
        }
      }

      .fields-list {
        max-height: 200px;
        overflow-y: auto;
        padding: 0 1rem 1rem 1rem;

        .field-item {
          display: flex;
          flex-direction: column;
          padding: 0.75rem;
          margin-bottom: 0.5rem;
          background-color: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.375rem;
          transition: all 0.2s ease;

          &:hover {
            border-color: #94c840;
            box-shadow: 0 1px 3px rgba(148, 200, 64, 0.1);
          }

          &:last-child {
            margin-bottom: 0;
          }

          .field-info {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 0.5rem;

            .field-key {
              font-weight: 600;
              color: #374151;
              font-size: 0.875rem;
            }

            .field-type {
              font-size: 0.75rem;
              color: #6b7280;
              background-color: #f3f4f6;
              padding: 0.25rem 0.5rem;
              border-radius: 0.25rem;
              text-transform: uppercase;
              font-weight: 500;
            }
          }

          .field-preview {
            font-size: 0.875rem;
            color: #6b7280;
            line-height: 1.4;
            font-style: italic;
          }
        }
      }
    }

    // Mode correction de langue
    .correction-mode-section {
      margin-bottom: 2rem;

      .correction-mode-toggle {
        display: flex;
        align-items: center;
        margin-bottom: 0.5rem;

        .correction-mode-label {
          margin-left: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
        }
      }

      .correction-mode-explanation {
        padding: 1rem;
        border: 1px solid #e5e7eb;
        border-radius: 0.5rem;
        background-color: #fafafa;

        .explanation-content {
          display: flex;
          align-items: flex-start;
          font-size: 0.875rem;
          color: #6b7280;
          margin-bottom: 0.75rem;

          i {
            color: #94c840;
            font-size: 1rem;
            margin-right: 0.5rem;
            margin-top: 0.125rem;
            flex-shrink: 0;
          }

          p {
            margin: 0;
            line-height: 1.5;
          }
        }

        .correction-warning {
          display: flex;
          align-items: center;
          font-size: 0.875rem;
          color: #f56565;
          font-weight: 500;

          i {
            color: #f56565;
            font-size: 1rem;
            margin-right: 0.5rem;
            flex-shrink: 0;
          }
        }
      }
    }

    // Texte de prévisualisation
    .text-preview {
      font-size: 0.875rem;
      line-height: 1.5;
      color: #374151;

      // Styles pour le contenu HTML
      :deep(p) {
        margin: 0.5rem 0;

        &:first-child {
          margin-top: 0;
        }

        &:last-child {
          margin-bottom: 0;
        }
      }

      :deep(a) {
        color: #3b82f6;
        text-decoration: underline;
      }

      :deep(strong) {
        font-weight: 600;
      }

      :deep(br) {
        line-height: 1.5;
      }
    }

    // Section de progression
    .progress-section {
      .progress-info {
        display: flex;
        align-items: center;
        font-size: 0.875rem;
        color: #6b7280;
        margin-bottom: 0.75rem;

        i {
          color: #94c840;
        }
      }
    }

    // Barre de progression personnalisée
    :deep(.custom-progress-bar) {
      .p-progressbar {
        height: 10px;
        border-radius: 5px;
        background-color: #e5e7eb;
        overflow: hidden;
        box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
      }

      .p-progressbar-value {
        background: linear-gradient(90deg, #94c840 0%, #7fb83d 100%) !important;
        border-radius: 5px;
        transition: width 0.4s ease;
        position: relative;

        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%);
          animation: shimmer 2s infinite;
        }
      }

      .p-progressbar-label {
        color: #ffffff !important;
        font-size: 0.75rem;
        font-weight: 500;
        margin-top: 0.2rem;
        text-align: center;
      }
    }
  }

  // Footer de la modale
  .modal-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.75rem;
    padding-top: 1rem;
  }
}

// Animation pour la barre de progression
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

// Styles globaux pour la modale
:deep(.p-dialog) {
  .p-dialog-header {
    padding: 1.5rem 1.5rem 1rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
    background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);

    .p-dialog-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #111827;
    }

    .p-dialog-header-icon {
      color: #6b7280;

      &:hover {
        color: #374151;
      }
    }
  }

  .p-dialog-content {
    padding: 1.5rem;
    background-color: #ffffff;
  }

  .p-dialog-footer {
    padding: 1rem 1.5rem 1.5rem 1.5rem;
    border-top: 1px solid #e5e7eb;
    background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
  }
}

// Amélioration des dropdowns
:deep(.p-dropdown) {
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  background-color: white;

  &:hover {
    border-color: #94c840;
  }

  &.p-focus {
    border-color: #94c840;
    box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.1);
  }

  .p-dropdown-trigger {
    color: #6b7280;
  }
}

// Amélioration des boutons
:deep(.p-button) {
  border-radius: 0.5rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &.p-button-success {
    background-color: #94c840;
    border-color: #94c840;

    &:hover {
      background-color: #7fb83d;
      border-color: #7fb83d;
    }
  }

  &.p-button-secondary {
    background-color: #f3f4f6;
    border-color: #d1d5db;
    color: #374151;

    &:hover {
      background-color: #e5e7eb;
      border-color: #9ca3af;
    }
  }
}
</style>
